<template>
  <div class="financial-records">
    <div class="page-header">
      <h2>财务记录</h2>
      <div class="header-actions">
        <a-button @click="handleExport" :loading="exportLoading">
          <template #icon>
            <DownloadOutlined />
          </template>
          导出Excel
        </a-button>
        <a-button type="primary" @click="handleCreate">
          <template #icon>
            <PlusOutlined />
          </template>
          新增记录
        </a-button>
      </div>
    </div>
    
    <!-- 汇总统计卡片 -->
    <a-card class="summary-card" :loading="summaryLoading">
      <a-row :gutter="32">
        <a-col :span="4">
          <a-statistic 
            title="总记录数" 
            :value="summaryData.sumCount || 0" 
            :value-style="{ fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic 
            title="收入记录" 
            :value="summaryData.incomeCount || 0" 
            :value-style="{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic 
            title="支出记录" 
            :value="summaryData.expenseCount || 0" 
            :value-style="{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic 
            title="收入总额" 
            :value="Number(summaryData.sumIncome || 0).toFixed(2)" 
            :value-style="{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }"
            prefix="¥"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic 
            title="支出总额" 
            :value="Number(summaryData.sumExpense || 0).toFixed(2)" 
            :value-style="{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }"
            prefix="¥"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic 
            title="收支余额" 
            :value="Number(summaryData.sumBalance || 0).toFixed(2)" 
            :value-style="{ 
              color: (summaryData.sumBalance || 0) >= 0 ? '#52c41a' : '#ff4d4f', 
              fontSize: '20px', 
              fontWeight: 'bold' 
            }"
            prefix="¥"
          />
        </a-col>
      </a-row>
    </a-card>
    
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="记录日期" name="dateRange">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            value-format="YYYYMMDD"
            style="width: 240px"
            :disabled-date="disabledDate"
          />
        </a-form-item>
        
        <a-form-item label="经办人" name="operatorName">
          <a-input
            v-model:value="searchForm.operatorName"
            placeholder="请输入经办人姓名"
            style="width: 150px"
          />
        </a-form-item>
        
        <a-form-item label="收支类型" name="transactionType">
          <a-select
            v-model:value="searchForm.transactionType"
            placeholder="请选择类型"
            style="width: 120px"
            allowClear
          >
            <a-select-option value="income">收入</a-select-option>
            <a-select-option value="expense">支出</a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="财务分类" name="categoryId">
          <a-select
            v-model:value="searchForm.categoryId"
            placeholder="请选择分类"
            style="width: 150px"
            allowClear
            @focus="loadCategories"
          >
            <a-select-option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="金额范围" name="amountRange">
          <a-input-group compact style="width: 200px">
            <a-input-number
              v-model:value="searchForm.amountMin"
              placeholder="最小金额"
              :precision="2"
              style="width: 50%"
            />
            <a-input-number
              v-model:value="searchForm.amountMax"
              placeholder="最大金额"
              :precision="2"
              style="width: 50%"
            />
          </a-input-group>
        </a-form-item>
        
        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            搜索
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>
    
    <a-card>
      <a-table
        :columns="columns"
        :data-source="recordsList"
        :pagination="pagination"
        :loading="loading"
        @change="handleTableChange"
        row-key="id"
        size="middle"
        bordered
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'transactionType'">
            <a-tag :color="record.transactionType === 'income' ? 'green' : 'red'">
              {{ record.transactionType === 'income' ? '收入' : '支出' }}
            </a-tag>
          </template>
          
          <template v-else-if="column.key === 'categoryName'">
            {{ getCategoryName(record.categoryId) }}
          </template>
          
          <template v-else-if="column.key === 'amount'">
            <span :style="{ color: record.transactionType === 'income' ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }">
              {{ record.transactionType === 'income' ? '+' : '-' }}{{ Number(record.amount).toFixed(2) }}
            </span>
          </template>
          
          <template v-else-if="column.key === 'recordDate'">
            {{ formatDate(record.recordDate) }}
          </template>
          
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleEdit(record)">
                编辑
              </a-button>
              <a-button type="link" size="small" @click="handleView(record)">
                查看
              </a-button>
              <a-popconfirm
                title="确定要删除这条财务记录吗?"
                ok-text="确定"
                cancel-text="取消"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>
    
    <!-- 查看详情模态框 -->
    <a-modal
      v-model:open="detailVisible"
      title="财务记录详情"
      :footer="null"
      width="600px"
    >
      <a-descriptions :column="2" bordered v-if="currentRecord">
        <a-descriptions-item label="记录日期">
          {{ formatDate(currentRecord.recordDate) }}
        </a-descriptions-item>
        <a-descriptions-item label="经办人">
          {{ currentRecord.operatorName }}
        </a-descriptions-item>
        <a-descriptions-item label="收支类型">
          <a-tag :color="currentRecord.transactionType === 'income' ? 'green' : 'red'">
            {{ currentRecord.transactionType === 'income' ? '收入' : '支出' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="财务分类">
          {{ getCategoryName(currentRecord.categoryId) }}
        </a-descriptions-item>
        <a-descriptions-item label="金额">
          <span :style="{ color: currentRecord.transactionType === 'income' ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }">
            {{ currentRecord.transactionType === 'income' ? '+' : '-' }}{{ Number(currentRecord.amount).toFixed(2) }}
          </span>
        </a-descriptions-item>
        <a-descriptions-item label="财务账户" :span="2">
          {{ currentRecord.financialAccount || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="备注" :span="2">
          {{ currentRecord.remark || '-' }}
        </a-descriptions-item>
      </a-descriptions>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { PlusOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import { financialApi } from '@/api/financial'
import { financialCategoryApi } from '@/api/financialCategory'
import dayjs from 'dayjs'

export default {
  name: 'FinancialRecords',
  components: {
    PlusOutlined,
    SearchOutlined,
    DownloadOutlined
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const summaryLoading = ref(false)
    const exportLoading = ref(false)
    const recordsList = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const categories = ref([])
    const detailVisible = ref(false)
    const currentRecord = ref(null)
    const summaryData = ref({})
    
    const searchForm = reactive({
      dateRange: undefined,
      operatorName: '',
      transactionType: undefined,
      categoryId: undefined,
      amountMin: undefined,
      amountMax: undefined
    })
    
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `共 ${total} 条记录`
    })
    
    const columns = [
      {
        title: '记录日期',
        key: 'recordDate',
        width: 120,
        align: 'center'
      },
      {
        title: '经办人',
        dataIndex: 'operatorName',
        width: 100,
        align: 'center'
      },
      {
        title: '收支类型',
        key: 'transactionType',
        width: 100,
        align: 'center'
      },
      {
        title: '财务分类',
        key: 'categoryName',
        width: 120,
        align: 'center'
      },
      {
        title: '金额',
        key: 'amount',
        width: 120,
        align: 'right'
      },
      {
        title: '财务账户',
        dataIndex: 'financialAccount',
        width: 130,
        ellipsis: true
      },
      {
        title: '备注',
        dataIndex: 'remark',
        width: 200,
        ellipsis: true
      },
      {
        title: '操作',
        key: 'action',
        width: 180,
        align: 'center',
        fixed: 'right'
      }
    ]
    
    const loadRecords = async () => {
      loading.value = true
      try {
        const params = {
          pageNo: pagination.current,
          pageSize: pagination.pageSize,
          operatorName: searchForm.operatorName || undefined,
          transactionType: searchForm.transactionType || undefined,
          categoryId: searchForm.categoryId || undefined,
          amountMin: searchForm.amountMin || undefined,
          amountMax: searchForm.amountMax || undefined
        }
        
        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0]
          params.endDate = searchForm.dateRange[1]
        }
        
        const result = await financialApi.list(params)
        recordsList.value = result.data.records || []
        pagination.total = result.data.total || 0
      } catch (error) {
        console.error('加载财务记录失败:', error)
        message.error('加载财务记录失败')
      } finally {
        loading.value = false
      }
    }
    
    const loadCategories = async () => {
      if (categories.value.length > 0) return
      
      try {
        const result = await financialCategoryApi.getTree()
        console.log('财务分类数据:', result.data) // 添加调试日志
        const flatCategories = []
        
        const flattenCategories = (cats, prefix = '') => {
          if (!cats || !Array.isArray(cats)) return
          
          cats.forEach(cat => {
            if (cat.status === 1 || cat.status === '1') { // 兼容数字和字符串类型
              flatCategories.push({
                id: cat.id,
                name: prefix + cat.name
              })
              if (cat.children && cat.children.length > 0) {
                flattenCategories(cat.children, prefix + cat.name + ' / ')
              }
            }
          })
        }
        
        flattenCategories(result.data)
        categories.value = flatCategories
        console.log('扁平化后的分类数据:', flatCategories) // 添加调试日志
      } catch (error) {
        console.error('加载财务分类失败:', error)
      }
    }
    
    const handleSearch = () => {
      pagination.current = 1
      loadRecords()
      loadSummary()
    }
    
    const handleReset = () => {
      Object.assign(searchForm, {
        dateRange: undefined,
        operatorName: '',
        transactionType: undefined,
        categoryId: undefined,
        amountMin: undefined,
        amountMax: undefined
      })
      pagination.current = 1
      loadRecords()
      loadSummary()
    }
    
    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadRecords()
    }
    
    const handleCreate = () => {
      router.push('/financial/edit')
    }
    
    const handleEdit = (record) => {
      router.push(`/financial/edit/${record.id}`)
    }
    
    const handleView = async (record) => {
      try {
        const result = await financialApi.detail(record.id)
        currentRecord.value = result.data
        detailVisible.value = true
      } catch (error) {
        console.error('加载记录详情失败:', error)
        message.error('加载记录详情失败')
      }
    }

    const handleDelete = async (record) => {
      try {
        await financialApi.delete({ id: record.id })
        message.success('删除成功')
        // 如果删除后当前页没有数据了,返回上一页
        if (recordsList.value.length === 1 && pagination.current > 1) {
          pagination.current--
        }
        loadRecords()
        loadSummary()
      } catch (error) {
        console.error('删除失败:', error)
        message.error(error.message || '删除失败')
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr || dateStr.length !== 8) return dateStr
      return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`
    }
    
    const disabledDate = (current) => {
      // 禁用今天之后的日期
      return current && current.isAfter(dayjs().endOf('day'))
    }
    
    const getCategoryName = (categoryId) => {
      if (!categoryId) return '-'
      const category = categories.value.find(cat => cat.id == categoryId)
      return category ? category.name : '-'
    }
    
    const loadSummary = async () => {
      summaryLoading.value = true
      try {
        const params = {
          operatorName: searchForm.operatorName || undefined,
          transactionType: searchForm.transactionType || undefined,
          categoryId: searchForm.categoryId || undefined,
          amountMin: searchForm.amountMin || undefined,
          amountMax: searchForm.amountMax || undefined
        }
        
        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0]
          params.endDate = searchForm.dateRange[1]
        }
        
        const result = await financialApi.getSummary(params)
        summaryData.value = result.data || {}
      } catch (error) {
        console.error('加载汇总统计失败:', error)
        message.error('加载汇总统计失败')
      } finally {
        summaryLoading.value = false
      }
    }
    
    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = {
          operatorName: searchForm.operatorName || undefined,
          transactionType: searchForm.transactionType || undefined,
          categoryId: searchForm.categoryId || undefined,
          amountMin: searchForm.amountMin || undefined,
          amountMax: searchForm.amountMax || undefined
        }
        
        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0]
          params.endDate = searchForm.dateRange[1]
        }
        
        const response = await financialApi.export(params)
        
        // 创建blob对象
        const blob = new Blob([response.data], {
          type: 'application/vnd.ms-excel;charset=utf-8'
        })
        
        // 创建下载链接
        const link = document.createElement('a')
        const url = window.URL.createObjectURL(blob)
        link.href = url
        
        // 生成文件名，格式：财务记录_yyyyMMdd_HHmmss.xlsx
        const now = new Date()
        const dateStr = now.getFullYear() + 
          String(now.getMonth() + 1).padStart(2, '0') + 
          String(now.getDate()).padStart(2, '0')
        const timeStr = String(now.getHours()).padStart(2, '0') + 
          String(now.getMinutes()).padStart(2, '0') + 
          String(now.getSeconds()).padStart(2, '0')
        
        link.download = `财务记录_${dateStr}_${timeStr}.xlsx`
        link.click()
        
        // 清理URL对象
        window.URL.revokeObjectURL(url)
        
        message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }
    
    onMounted(async () => {
      await loadCategories()
      loadRecords()
      loadSummary()
    })
    
    return {
      loading,
      summaryLoading,
      exportLoading,
      recordsList,
      categories,
      detailVisible,
      currentRecord,
      summaryData,
      searchForm,
      pagination,
      columns,
      handleSearch,
      handleReset,
      handleTableChange,
      handleCreate,
      handleEdit,
      handleView,
      handleDelete,
      handleExport,
      loadCategories,
      formatDate,
      disabledDate,
      getCategoryName
    }
  }
}
</script>

<style scoped>
.financial-records {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.summary-card {
  margin-bottom: 16px;
}

.filter-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}
</style>